import debugConstructor from 'debug'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import pick from 'lodash/pick'

import * as snapshotActions from 'common/builder/actions/builderSnapshotActions'
import { patchFlowDraft } from 'common/builder/actions/manager/managerFlowActionsTyped'
import { setFlowDraft } from 'common/builder/actions/manager/setFlowDraft'
import * as ActionTypes from 'common/builder/constants/builderReduxActionTypes'
import BuilderStateModel from 'common/builder/models/BuilderState'
import builderSelectors from 'common/builder/selectors/builder'
import * as snapshotSelectors from 'common/builder/selectors/builder/snapshotSelectors'
import * as managerSelectors from 'common/builder/selectors/managerSelectors'
import FlowModel from 'common/flow/models/Flow'
import errorTrackingService from 'utils/services/errorTrackingService'
import { alertWithHotjarEventFactory } from 'utils/services/hotjar'

const debug = debugConstructor('manager:autoSave')

const alertWithHotjarEvent = alertWithHotjarEventFactory('flow_builder_error')

const AUTO_SAVE_TRY_DELAY = 0.5 * 1000
const AUTO_SAVE_OUT_DATE_PERIOD = 10 * 1000
const AUTO_SAVE_DEBOUNCE_PERIOD = 2 * 1000

const AUTO_SAVE_TIMERS = {}

/**
 * @param managerId
 */
export function enableAutoSave(managerId) {
  return async (dispatch, getState) => {
    // Save all initial values of rootId, coordinates, and contents
    // They will be used to determine minimal changes for the patch
    // todo combine into one function
    dispatch(resetLastChanged(managerId))
    dispatch(commitLastRootId(managerId))
    dispatch(commitLastCoordinates(managerId))
    dispatch(commitLastContentHashMap(managerId))

    dispatch(autoSaveLoop(managerId))
  }
}

/**
 * @param managerId
 */
export function disableAutoSave(managerId) {
  return (dispatch, getState) => {
    clearTimeout(AUTO_SAVE_TIMERS[managerId])
  }
}

let prevLastChanged = {}
let prevState = {}

/**
 * @param managerId
 */
export function autoSaveLoop(managerId) {
  return (dispatch, getState) => {
    prevState[managerId] = getState()
    AUTO_SAVE_TIMERS[managerId] = setInterval(() => {
      dispatch(tryAutoSave(managerId))
    }, AUTO_SAVE_TRY_DELAY)
  }
}

/**
 * @param managerId
 * @param options - {force}
 */
export function tryAutoSave(managerId, options = {}) {
  return (dispatch, getState) => {
    const state = getState()

    const manager = managerSelectors.getById(state, managerId)
    if (!manager) {
      return
    }
    const builderId = manager.builderId
    const builder = builderSelectors.builderState.getById(state, builderId)
    const flow = managerSelectors.getFlow(state, managerId)

    if (!builder || !builder.isReady) {
      return debug("builder isn't ready")
    }

    if (!BuilderStateModel.hasChanges(builder)) {
      return debug('skip, no changes')
    }

    if (manager.isPublishing) {
      return debug('skip, publish in progress')
    }

    if (manager.lastSaved && manager.lastSaved > builder.lastChanged) {
      prevState[managerId] = getState()
      return debug('skip, no changes since last autosave')
    } else {
      const isRepeatSave = builder.lastChanged === prevLastChanged[managerId]
      const isSnapshotInProgress = snapshotSelectors.getSnapshotInstallationStatus(state, builderId)
      const snapshotIndex = snapshotSelectors.getSnapshotIndex(state, builderId)
      const prevSnapshotIndex =
        prevState[managerId] && snapshotSelectors.getSnapshotIndex(prevState[managerId], builderId)
      const isInitialFlow = builder.isInitializedBlank && !manager.lastSaved
      const isInitialUndo = snapshotIndex === 0 && prevSnapshotIndex === 0
      const isNotUndoAction = snapshotIndex === prevSnapshotIndex

      if (
        !isRepeatSave &&
        !isSnapshotInProgress &&
        !isInitialFlow &&
        (isInitialUndo || isNotUndoAction)
      ) {
        dispatch(snapshotActions.generateBuilderSnapshot(prevState[managerId], builderId))
      }
      prevState[managerId] = getState()
    }

    prevLastChanged[managerId] = builder.lastChanged

    if (manager.isAutoSaving) {
      return debug('skip, auto save is in progress')
    }

    // Skip this condition and proceed directly to saving in case:
    // - if this is the first autosave (no draft)
    // - if called with the force flag
    // - if an error occurred earlier
    // - if more than AUTO_SAVE_OUT_DATE_PERIOD seconds have passed since the last save
    // - if the user has not edited anything for more than AUTO_SAVE_DEBOUNCE_PERIOD seconds
    const hasDraft = FlowModel.hasDraft(flow)
    const draftOutDate = manager.lastSaved + AUTO_SAVE_OUT_DATE_PERIOD < Date.now()
    const editingInProgress = builder.lastChanged + AUTO_SAVE_DEBOUNCE_PERIOD > Date.now()
    if (hasDraft && !options.force && !manager.saveError && !draftOutDate && editingInProgress) {
      return debug('skip, debounce')
    }

    return dispatch(performAutoSave(managerId))
  }
}

/**
 * @param managerId
 */
export function performAutoSave(managerId) {
  return async (dispatch, getState) => {
    const state = getState()

    const manager = managerSelectors.getById(state, managerId)
    if (!manager) {
      return
    }

    try {
      // Uncomment if the property managerState.isAutoSaving is needed
      dispatch({ type: ActionTypes.MANAGER_AUTO_SAVE_REQUEST, managerId })

      const lastSaved = Date.now()
      const changes = managerSelectors.getBuilderChanges(state, managerId)
      const { patch, coordinatesPatch } = changes

      if (isEmpty(patch) && isEmpty(coordinatesPatch)) {
        debug('skip, no actual changes since last autosave')
      } else {
        const builderId = manager.builderId
        const builder = builderSelectors.builderState.getById(state, builderId)

        if (builder.isInitializedBlank && !manager.lastSaved) {
          // If there is neither published nor draft content in the flow -
          // the builder is initialized with new content with an empty block
          //
          // If several users open such a flow simultaneously
          // they will both create new contents that will not be synchronized with each other
          //
          // Therefore, when the first autosave of the builder with new content occurs,
          // a request is made for a complete replacement of the draft,
          // so that users who have this flow open
          // receive a notification to update the entire draft
          // this update replaces new contents with the actual draft for all users
          await dispatch(setFlowDraft(manager.id, patch, coordinatesPatch))
          debug(`success setFlowDraft at ${lastSaved}`)
        } else {
          if (!manager.flowId) {
            throw new Error('Failed to save. Please refresh the page.')
          }
          await dispatch(patchFlowDraft(manager.flowId, patch, coordinatesPatch))
          debug(`success patchFlowDraft at ${lastSaved}`)
        }
      }

      const { contentHashMap, rootId, coordinates } = changes
      dispatch({
        type: ActionTypes.MANAGER_AUTO_SAVE_SUCCESS,
        managerId,
        lastSaved,
        contentHashMap,
        rootId,
        coordinates,
      })
    } catch (err) {
      const manager = managerSelectors.getById(state, managerId)
      const saveError = `${err.message}`

      errorTrackingService.trackError(err, {
        fingerprint: 'auto-save-flow-error',
        managerId,
        builderId: manager?.builderId,
      })

      debug(`error`, err.message)
      dispatch({ type: ActionTypes.MANAGER_AUTO_SAVE_ERROR, managerId, saveError })

      if (manager.saveError !== saveError) {
        alertWithHotjarEvent(err.message, 'danger')
      }
    }
  }
}

/**
 * @param managerId
 */
export function resetLastChanged(managerId) {
  return {
    type: ActionTypes.RESET_MANAGER_LAST_SAVED,
    managerId,
  }
}

/**
 * @param managerId
 * @param updateOnlyContentIds - update only the specified content ids
 */
export function commitLastContentHashMap(managerId, updateOnlyContentIds) {
  return async (dispatch, getState) => {
    const state = getState()

    const manager = managerSelectors.getById(state, managerId)
    const builder = managerSelectors.getBuilder(state, managerId)
    if (!builder) {
      return
    }

    const fullContentHashMap = managerSelectors.getBuilderContentHashMap(state, managerId)
    let contentHashMap = fullContentHashMap

    if (isArray(updateOnlyContentIds)) {
      const contentHashMapPatch = pick(fullContentHashMap, ...updateOnlyContentIds)
      contentHashMap = { ...(manager.lastContentHashMap || {}), ...contentHashMapPatch }
    }

    if (builder.isInitializedBlank) {
      const rootId = builderSelectors.builderState.getRootNodeId(state, builder.id)
      contentHashMap[rootId] && delete contentHashMap[rootId]
    }

    dispatch({
      type: ActionTypes.UPDATE_MANAGER_LAST_CONTENT_HASH_MAP,
      managerId,
      contentHashMap,
    })
  }
}

/**
 * @param managerId
 */
export function commitLastRootId(managerId) {
  return async (dispatch, getState) => {
    const state = getState()

    const builder = managerSelectors.getBuilder(state, managerId)
    if (!builder) {
      return
    }

    let rootId = null

    if (!builder.isInitializedBlank) {
      rootId = builderSelectors.builderState.getRootNodeId(state, builder.id)
    }

    dispatch({
      type: ActionTypes.UPDATE_MANAGER_LAST_ROOT_ID,
      managerId,
      rootId,
    })
  }
}

/**
 * @param managerId
 * @param updateOnlyNodeIds - update only the specified node ids
 */
export function commitLastCoordinates(managerId, updateOnlyNodeIds) {
  return async (dispatch, getState) => {
    const state = getState()

    const builder = managerSelectors.getBuilder(state, managerId)
    if (!builder) {
      return
    }

    let coordinates = builder.coordinates
    if (isArray(updateOnlyNodeIds)) {
      const coordinatesPatch = pick(builder.coordinates, ...updateOnlyNodeIds)
      coordinates = { ...(builder.coordinates || {}), ...coordinatesPatch }
    }

    dispatch({
      type: ActionTypes.UPDATE_MANAGER_LAST_COORDINATES,
      managerId,
      coordinates,
    })
  }
}
